<template>
	<Head title="Coleta de Foto"></Head>

	<div class="container">
		<div class="row align-items-center mt-10 mb-10 row-logo">
			<div class="col mx-auto text-center">
				<img
					alt="Logo Habilite-se"
					src="/habil/images/logo.svg"
					class="h-35px" />
			</div>
		</div>

		<div class="row align-items-center row-card">
			<div class="col-sm-10 col-md-9 mx-auto text-center">
				<div class="card">
					<div class="card-body" style="padding: 26px 0px 26px 0px">
						<div class="text-center mb-5">
							<h2 class="m-0">Assinatura Digital</h2>
							<p
								style="font-size: 15px; color: #747a7e"
								class="fw-bolder fs-5 text-gray-600">
								Sua prova será assinada digitalmente.
							</p>
						</div>
						<div v-if="photo_error" class="error m-5">
							<div
								class="alert alert-danger alert-dismissible d-flex flex-column flex-sm-row p-5 mb-10">
								<div class="d-flex flex-column">
									<span>{{ error_message }}</span>
								</div>
							</div>
						</div>
						<div class="row" style="padding: 0px 26px 0px 26px">
							<div class="col-md-12">
								<div
									id="box-photo"
									class="signature-media-box">
									<div
										v-if="btn_capture_photo && !photo_completed"
										class="exam-media-overlay exam-countdown-overlay"
										style="z-index: 1">
										<strong>
											{{ waiting_photo_seconds }}
										</strong>
									</div>
									<div
										class="blockui-overlay d-none"
										style="z-index: 1">
										<span
											class="spinner-border text-primary"></span>
									</div>
										<div v-if="props.mock_webcam">
											<img
												v-if="b64_photo && !photo_completed"
												:src="`data:image/jpeg;base64,${b64_photo}`"
												class="signature-camera-frame signature-camera-media" />
										</div>
										<div v-else>
											<div
												v-show="!photo_completed"
												class="signature-camera-frame">
												<div
													v-if="
														props.browser_webcam_streaming_enabled
													"
													class="signature-camera-media">
													<video
														ref="local_camera_video"
														autoplay
														muted
														playsinline
														class="signature-camera-video"></video>
												</div>
												<div
													v-else
													class="signature-camera-media">
													<webrtc_video_player
														:auto="false"
														@started="camera_ready = true"
														:width="300"
														:height="300"
														class="signature-camera-player"
														style="height: 100%; width: 100%"
														ref="video_player_component"
														:source_url="
															props.webcam_webrtc_streaming_source
														"
														:streaming_auth_credential="
															props.streaming_auth_credential
														" />
												</div>
											</div>
										</div>
										<img
											v-show="photo_completed"
											id="photo-preview"
											:src="`data:image/jpeg;base64,${b64_photo}`"
											class="signature-camera-frame signature-camera-media" />
									</div>
								<button
									v-if="!photo_completed && !btn_capture_photo"
									:disabled="btn_capture_photo || !can_capture_photo"
									@click="capturePhoto"
									id="btn-capture-photo"
									class="btn btn-success mb-2"
									style="background-color: #022d62">
									<div
										v-if="!btn_capture_photo"
										class="fw-bolder fs-5">
										<div class="fa fa-lg fa-camera"></div>
										&nbsp;&nbsp;Capturar Foto
									</div>
								</button>
								<button
									v-if="btn_capture_photo && photo_completed"
									id="btn-tryagain-photo"
									class="btn btn-danger mb-2"
									@click="resetProcess">
									<span class="fw-bolder fs-5">
										<span
											class="fa fa-lg fa-refresh"></span>
										Refazer captura
									</span>
								</button>
								<button
									id="submit-photo"
									:disabled="!photo_completed"
									class="btn btn-success w-100 mt-5"
									type="submit"
									@click="sendPhoto">
									Assinar e ver resultado
									<span
										class="fa fa-lg fa-arrow-right"></span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { Head } from '@inertiajs/vue3';
	import { ref, Ref, onMounted, onBeforeUnmount, computed } from 'vue';
	import axios from 'axios';
	import { commonStore } from '../../stores/common';
	import { checkConnection } from '../../websockets/WebsocketVerification';
    import webrtc_video_player from '../../components/Widgets/webrtc_video_player.vue';


	const common = commonStore();
	const props = defineProps({
		user: { type: Object, required: true },
		csrf_token: { type: String, required: true },
		action_store_photo: { type: String, required: true },
		next_step_route: { type: String, required: true },
		enrollment_test: { type: Object, required: true },
		img_mocked: { type: String, required: true },
		mock_webcam: { type: Boolean, required: true },
		computer_uid: { type: String, required: true },
		is_ws_verification: { type: Boolean, required: true },
		logout_url: { type: String, required: true },
		descricao_computer: { type: String, required: true },
		is_agent_support_ws_verification: { type: Boolean, required: true },
        webcam_webrtc_streaming_source: { type: String, required: true },
        streaming_auth_credential: { type: String, required: true },
        browser_webcam_streaming_enabled: { type: Boolean, required: false, default: false },
    });

	const btn_capture_photo: Ref<boolean> = ref(false);
	const waiting_photo_seconds: Ref<number> = ref(1);
	const photo_completed: Ref<boolean> = ref(false);
	const b64_photo: Ref<string> = ref('');
	const photo_error: Ref<boolean> = ref(false);
	const error_message: Ref<string> = ref('');
	const can_capture_photo = computed(() => {
		if (props.mock_webcam) {
			return true;
		}

		return camera_ready.value;
	});

	const connectionSpinner = ref(true);
	const connectionStatus = ref({
		connection: false,
		verified: false,
	});
    const video_player_component = ref<InstanceType<typeof webrtc_video_player> | null>(null);
	const local_camera_video = ref<HTMLVideoElement | null>(null);
	const local_camera_stream = ref<MediaStream | null>(null);
	const camera_ready = ref(false);

	onMounted(() => {
        if (props.mock_webcam) {
            b64_photo.value = props.img_mocked
        } else if (props.browser_webcam_streaming_enabled) {
            startLocalCamera();
        } else if (!props.browser_webcam_streaming_enabled) {
            video_player_component.value?.start()
        }

        common.startKeyUp();

        checkConnection(props, connectionStatus, connectionSpinner);

		setInterval(() => {
			checkConnection(props, connectionStatus, connectionSpinner);
		}, 25000);
	});

	onBeforeUnmount(() => {
		stopLocalCamera();
	});

	async function startLocalCamera() {
		try {
			local_camera_stream.value = await navigator.mediaDevices.getUserMedia({
				video: true,
				audio: false,
			});

			if (local_camera_video.value === null) {
				return;
			}

			local_camera_video.value.srcObject = local_camera_stream.value;
			await local_camera_video.value.play();
			camera_ready.value = true;
		} catch {
			camera_ready.value = false;
			photo_error.value = true;
			error_message.value = 'Não foi possível acessar a câmera.';
		}
	}

	function stopLocalCamera() {
		local_camera_stream.value?.getTracks().forEach((track) => track.stop());
		local_camera_stream.value = null;
	}

	function capturePhoto() {
		btn_capture_photo.value = true;
		waitingSetInterval();
	}

	function timerWaitingPhotoSeconds() {
		if (props.mock_webcam) {
			waiting_photo_seconds.value = 1;
		} else {
			waiting_photo_seconds.value = 5;
		}
	}

	function resetProcess() {
		timerWaitingPhotoSeconds();
		btn_capture_photo.value = false;
		photo_completed.value = false;
		b64_photo.value = '';
		capturePhoto();
	}

    async function takePhoto() {
        if (props.mock_webcam) {
            return props.img_mocked;
        }

        if (props.browser_webcam_streaming_enabled) {
            return takeLocalCameraPhoto();
        }

        return video_player_component.value?.takePhoto();
    }

	function takeLocalCameraPhoto() {
		const video = local_camera_video.value;

		if (video === null || video.videoWidth === 0 || video.videoHeight === 0) {
			return null;
		}

		const canvas = document.createElement('canvas');
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		const context = canvas.getContext('2d');

		if (context === null) {
			return null;
		}

		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		return canvas.toDataURL('image/jpeg').split(',')[1];
	}

	function waitingSetInterval() {
		timerWaitingPhotoSeconds();
		let timer: Ref<NodeJS.Timeout> = ref(
			setInterval(async () => {
				if (waiting_photo_seconds.value > 0) {
					waiting_photo_seconds.value =
						waiting_photo_seconds.value - 1;
				} else {
                    const photo = await takePhoto();

                    if (photo) {
                        b64_photo.value = photo;
                    }
                    photo_completed.value = true;
                    clearInterval(timer.value);
				}
			}, 1000)
		);
	}

	function sendPhoto() {
		axios
			.post(props.action_store_photo, {
				responsePhoto: b64_photo.value,
				responseTestId:  props.enrollment_test.id
			})
			.then(function (response) {
				if (response.data?.success !== true) {
					photo_error.value = true;
					error_message.value =
						response.data?.message ??
						'Não foi possível enviar a foto. Tente novamente.';
					return;
				}

				window.location = props.next_step_route as unknown as Location;
			})
			.catch(function (error) {
				photo_error.value = true;
				error_message.value =
					error.response?.data?.error?.responsePhoto?.[0] ??
					error.response?.data?.error?.responseTestId?.[0] ??
					'Não foi possível enviar a foto. Tente novamente.';
        });
	}

</script>

<style>
	.mask {
		mask-image: radial-gradient(
			ellipse 75% 95%,
			black 50%,
			rgba(0, 0, 0, 0.4) 51%
		);
		-webkit-mask-image: radial-gradient(
			ellipse 75% 95%,
			black 50%,
			rgba(0, 0, 0, 0) 51%
		);
		mask-border-width: 25%;
	}

	.error_email{
		border-color:#d9214e;
	}

    .signature-media-box {
        align-items: center;
        display: flex;
        height: 300px;
        justify-content: center;
        margin: 0 auto 0.5rem;
        position: relative;
        width: 300px;
    }

    .signature-camera-frame {
        mask-image: radial-gradient(
            ellipse 75% 95%,
            black 50%,
            rgba(0, 0, 0, 0.4) 51%
        );
        -webkit-mask-image: radial-gradient(
            ellipse 75% 95%,
            black 50%,
            rgba(0, 0, 0, 0) 51%
        );
        height: 300px;
        mask-border-width: 25%;
        overflow: hidden;
        width: 300px;
    }

	    .signature-camera-media,
	    .signature-camera-video {
	        height: 100%;
	        width: 100%;
	    }

	    .signature-camera-player,
	    .signature-camera-player > div,
	    .signature-camera-player .video {
	        height: 100% !important;
	        width: 100% !important;
	    }

	    .signature-camera-media,
	    .signature-camera-video,
	    .signature-camera-player .video {
	        object-fit: fill;
	    }

	.signature-media-box .exam-media-overlay {
		align-items: center;
		background: rgba(255, 255, 255, 0.76);
		border-radius: 10px;
		display: flex;
		inset: 0;
		justify-content: center;
		position: absolute;
	}

	.signature-media-box .exam-countdown-overlay {
		background: rgba(2, 45, 98, 0.72);
		color: #ffffff;
		flex-direction: column;
		font-weight: 800;
		gap: 8px;
		text-align: center;
	}

	.signature-media-box .exam-countdown-overlay strong {
		align-items: center;
		background: #ffffff;
		border-radius: 999px;
		color: #022d62;
		display: inline-flex;
		font-size: 56px;
		height: 104px;
		justify-content: center;
		line-height: 1;
		width: 104px;
	}
</style>
